import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface ChecklistItemToggleRowProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  title: string;
  description: string;
  /** Rendered directly beneath the row, joined to it, when the toggle is on. */
  children?: React.ReactNode;
  /** Tints the row and squares off its bottom so `children` reads as attached. */
  lit?: boolean;
}

/**
 * One switch row in the checklist item form: Required, Allow photo uploads,
 * Example photos.
 *
 * ⚠️ THE TOGGLE IS ON THE LEFT, AND THAT IS THE POINT OF THIS COMPONENT.
 * These were three different shapes: two bare checkboxes with their labels to
 * the right, and one card with the control floated to the far right edge. An
 * admin ticked "Allow photo attachments", saw the words "Example photos"
 * appear underneath, and could not find the control for it — because it was
 * across the other side of the row, past the description, where nothing else
 * in the form had ever put one.
 *
 * Control first, then what it does. Scanning down the column, every switch is
 * in the same place.
 *
 * ⚠️ ONE COMPONENT, RENDERED SIX TIMES. `ChecklistItemManager.tsx` carries two
 * near-duplicate item forms, so three controls become six copies. That file's
 * whole history is changes landing in one form and not the other: EXP-1.4
 * exists because the photo checkbox was inert in both for as long as it had
 * existed. A single definition makes a half-change impossible.
 */
export function ChecklistItemToggleRow({
  id,
  checked,
  onCheckedChange,
  title,
  description,
  children,
  lit = false,
}: ChecklistItemToggleRowProps) {
  const joined = lit && checked && Boolean(children);

  return (
    <div className="flex flex-col">
      <div
        className={[
          'flex items-start gap-3 rounded-lg border bg-background px-3 py-2.5',
          joined ? 'rounded-b-none border-b-0 bg-primaryLight' : 'border-border',
        ].join(' ')}
        style={joined ? { borderColor: TINT_BORDER } : undefined}
      >
        {/* ⚠️ First in the DOM as well as first on screen, so the tab order
            reaches the control before its explanation. */}
        <Switch
          id={id}
          checked={checked}
          onCheckedChange={onCheckedChange}
          className="mt-0.5 flex-none"
        />
        {/* ⚠️ `Label` is a flex row by default (`flex items-center gap-2`), so
            title and description sit side by side rather than stacked. That
            reads well for these short descriptions and is kept deliberately —
            but `flex-wrap` is set explicitly so a narrow panel drops the
            description to its own line instead of squeezing both. Relying on
            the primitive's default without saying so is how a layout becomes
            accidental. */}
        <Label
          htmlFor={id}
          className="min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0 cursor-pointer font-normal"
        >
          <span className="text-xs font-semibold text-foreground">{title}</span>
          <span className="text-[11px] font-normal text-muted-foreground">{description}</span>
        </Label>
      </div>

      {joined && children}
    </div>
  );
}

/**
 * The mid-tint for a lit row's border, derived from the tenant's own primary.
 *
 * ⚠️ Inline rather than a utility class. `tailwind.config.cjs` maps `primary`
 * straight to `var(--primary)`, which holds a HEX at runtime, and Tailwind's
 * `/40` opacity modifier needs a bare channel triplet it can wrap in
 * `hsl(... / <alpha-value>)`. `border-primary/40` therefore compiles to invalid
 * CSS and is dropped silently — measured in the browser as the default grey.
 */
export const TINT_BORDER = 'color-mix(in srgb, var(--primary) 40%, transparent)';
